import { useState, useEffect, useMemo } from "react";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import useWikiCases from "./useWikiCases";
import { calcMargin } from "./utils";
import { GRID_SECTIONS } from "./constants";
import Banner from "./Banner";
import Overview from "./Overview";
import Grid from "./Grid";

const MainWiki = () => {
  const {
    caseNames,
    currentCompany,
    setCurrentCompany,
    isLoading: casesLoading,
  } = useWikiCases();

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.company !== undefined) setCurrentCompany(e.detail.company);
    };
    window.addEventListener("sidebar:wiki-company-selected", handler);
    return () =>
      window.removeEventListener("sidebar:wiki-company-selected", handler);
  }, [setCurrentCompany]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("sidebar:select-company", {
        detail: { company: currentCompany },
      }),
    );
  }, [currentCompany]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("sidebar:refresh-cases"));
  }, [caseNames]);

  const [baseMargin, setBaseMargin] = useState(() =>
    calcMargin(window.innerWidth),
  );
  const [currentSubSection, setCurrentSubSection] = useState("Overview");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const openSearch = () =>
    window.dispatchEvent(new CustomEvent("wiki:add-company"));

  useEffect(() => {
    setCurrentSubSection("Overview");
  }, [currentCompany]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.subSection) {
        const section = e.detail.subSection;
        if (section !== currentSubSection) {
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentSubSection(section);
            setIsTransitioning(false);
          }, 150);
        }
      }
    };
    window.addEventListener("sidebar:wiki-subsection-selected", handler);
    return () =>
      window.removeEventListener("sidebar:wiki-subsection-selected", handler);
  }, [currentSubSection]);

  useEffect(() => {
    const handleResize = () => {
      const m = calcMargin(window.innerWidth);
      setBaseMargin((prev) => (prev !== m ? m : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubSectionChange = (section) => {
    if (section === currentSubSection) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSubSection(section);
      setIsTransitioning(false);
    }, 150);
  };

  const content = useMemo(() => {
    if (!currentCompany) return null;
    if (currentSubSection === "Overview") {
      return (
        <Overview
          currentCase={currentCompany}
          setCurrentSubSection={handleSubSectionChange}
          currentCompany={currentCompany}
          baseMargin={baseMargin}
        />
      );
    }
    if (GRID_SECTIONS.includes(currentSubSection)) {
      return (
        <Grid
          tileType={currentSubSection}
          currentCase={currentCompany}
          baseMargin={baseMargin}
          onBack={() => handleSubSectionChange("Overview")}
        />
      );
    }
    return null;
  }, [currentCompany, currentSubSection, baseMargin]);

  if (!currentCompany) {
    return (
      <div className="flex h-full items-center justify-center font-poppins font-normal">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col font-poppins font-normal text-mainText">
      {currentSubSection === "Overview" && (
        <Banner currentCompany={currentCompany} baseMargin={baseMargin} />
      )}
      <div className="flex-1">
        <div
          className="h-full w-full"
          style={{
            transition: "opacity 0.15s ease-in-out",
            opacity: isTransitioning ? 0 : 1,
          }}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

export default MainWiki;
